import { liveCheckSelectors } from 'wanderu-ducks-live-check';
import { createAppSelector } from 'wanderu-ducks-utils';

import {
    selectAllTripIds,
    selectLegIds,
    selectTripArriveData,
    selectTripCarrierData,
    selectTripData,
    selectTripDataPricing,
    selectTripDepartData,
    selectTripDuration,
    selectTripInformation,
    selectTripItinerary,
    selectTripNumberOfMidnightsPassed,
} from './baseTripSelectors';
import {
    buildLocalizedTime,
    calculatePricingInUSD,
    flattenTripModes,
    getAccessibilityData,
    getPromotions,
    getRoutedDuration,
    getRoutedTripModes,
    getSingleDuration,
    getSingleTripPricing,
    isRoutedTrip,
} from './helpers';

const FEATURE_BLACKLIST = [
    'luxury',
    'bikes_allowable',
    'next_gen',
    'pets_allowable',
];

function mapTripData(tripData, pricingData) {
    if (!tripData || !tripData.trip_id) {
        return undefined;
    }

    const {
        _perSeatPricing,
        fees,
        pricing,
    } = pricingData;
    const {
        accessibility,
        arrive_datetime: arriveDateTime,
        arrive_datetime_utc: arriveDateTimeUTC,
        arrive_id: arriveId,
        arrive_lat_long: arriveLatLong,
        arrive_name: arriveName,
        arrive_timezone: arriveTimezone,
        arrive_timezone_offset: arriveTimezoneOffset,
        carrier,
        created,
        depart_datetime: departDateTime,
        depart_datetime_utc: departDateTimeUTC,
        depart_id: departId,
        depart_lat_long: departLatLong,
        depart_name: departName,
        depart_timezone: departTimezone,
        depart_timezone_offset: departTimezoneOffset,
        features = {},
        promotions,
        reviews,
        source,
        trip_id: tripId,
        triplegs,
        vehicle_types: modes,
    } = tripData;

    const sortedAndFilteredFeatures = Object.keys(features)
        .filter((key) => Boolean(features[key]) && !FEATURE_BLACKLIST.includes(key))
        .sort((a, b) => a.localeCompare(b, 'en')); // consistent order across all locales
    const isRouted = isRoutedTrip(tripData);
    const tripLegs = mapTripLegs(triplegs, pricingData);
    const tripModes = isRouted
        ? getRoutedTripModes(tripLegs.filtered)
        : (flattenTripModes(modes) || []); // backend bug
    const appliedPromotions = getPromotions(promotions, isRouted);
    const accessibilityInfo = getAccessibilityData(accessibility);

    const pricingInUSD = calculatePricingInUSD(_perSeatPricing);

    const localizedArriveDateTime = buildLocalizedTime({
        dateTime: arriveDateTime,
        dateTimeUTC: arriveDateTimeUTC,
        timezone: arriveTimezone,
        timezoneOffset: arriveTimezoneOffset,
    });

    const localizedDepartDateTime = buildLocalizedTime({
        dateTime: departDateTime,
        dateTimeUTC: departDateTimeUTC,
        timezone: departTimezone,
        timezoneOffset: departTimezoneOffset,
    });

    const duration = isRoutedTrip(tripData)
        ? getRoutedDuration(tripData)
        : getSingleDuration(tripData);

    const isLuxury = features.luxury || false;
    const isNextGen = features?.next_gen || false;

    return {
        _priceInUSDForSorting: pricingInUSD,
        accessibility: accessibilityInfo,
        arriveDateTimeUTC,
        arriveId,
        arriveLatLong,
        arriveName,
        carrierCode: carrier,
        created,
        departDateTimeUTC,
        departId,
        departLatLong,
        departName,
        duration,
        features: sortedAndFilteredFeatures,
        fees,
        isLuxury,
        isNextGen,
        isRouted,
        localizedArriveDateTime,
        localizedDepartDateTime,
        pricing,
        promotions: appliedPromotions,
        reviews,
        transfers: tripData.transfers,
        tripId,
        tripLegs,
        tripModes,
        tripSource: source?.type || 'unknown',
    };
}

function mapTripLegs(tripLegs, pricingData) {
    if (!tripLegs || !tripLegs.length) {
        return [];
    }

    let legIdx = 0;
    return tripLegs.reduce((acc, segment) => {
        const legPricingData = {
            _perSeatPricing: [pricingData._perSeatPricing[legIdx]],
            pricing: [pricingData.pricing[legIdx]],
        };

        const leg = segment.trip_id
            /* select data out of these too - make sure everyone has the same shape */
            ? mapTripData(segment, legPricingData)
            : segment;

        /* "all" means "both travel legs and transfer/layover instances".  when trip
         * details for a routed trip are viewed, we show travel legs separated by
         * transfers and layovers */
        acc.all = [
            ...acc.all,
            leg,
        ];

        if (segment.trip_id) {
            legIdx += 1;
            /* "filtered" means "with all the transfers and layovers taken out".  we do a
             * few transforms which only apply to actual legs of trips and not the
             * layovers/transfers, so this is purely for convenience */
            acc.filtered = [
                ...acc.filtered,
                leg,
            ];
        }

        return acc;
    }, { all: [], filtered: [] });
}

const selectTrip = createAppSelector(
    [
        (state, tripId) => selectTripData(state, tripId),
        (state, tripId, seats, wheelchairs) => selectTripPrices(state, tripId, seats, wheelchairs),
    ],
    (tripData, pricing) => mapTripData(tripData, pricing),
);

function selectAllTrips(state) {
    const tripIds = selectAllTripIds(state);
    return tripIds.reduce((obj, tripId) => ({
        ...obj,
        [tripId]: selectTrip(state, tripId),
    }), {});
}

function selectHasFlight(state, tripId) {
    const trips = selectTripSet(state, tripId);
    return trips.some((trip) => trip.tripModes.includes('flight'));
}

function selectTripSet(state, tripIds) {
    const legIds = selectLegIds(state, tripIds);

    if (!legIds || !legIds.length) {
        return [];
    }

    return legIds
        .map((legId) => selectTrip(state, legId))
        .filter(Boolean);
}

function selectLiveCheckPrices(state, opts) {
    const totalPricing = selectLiveCheckTotalPrices(state, opts) || [];
    if (totalPricing.length === 0) {
        return undefined;
    }

    let mappedPricing = totalPricing;

    if (opts.showTotalPrice) {
        mappedPricing = totalPricing.map(pricingObj => {
            return Object.entries(pricingObj).reduce((acc, [cur, pricing]) => {
                acc[cur] = acc[cur] ?? { price: pricing.price };
                const totalFees = Object.values(pricing.fees || {})
                    .reduce((total, feeValue) => total + feeValue, 0);
                const totalTaxes = Object.values(pricing.taxes || {})
                    .reduce((total, taxValue) => total + taxValue, 0);
                acc[cur].price += totalFees;
                acc[cur].price += totalTaxes;

                return acc;
            }, {});
        });
    }

    return mappedPricing.reduce((arr, lcPricing) => ([
        ...arr,
        ...getSingleTripPricing(lcPricing),
    ]), []);
}

function selectLiveCheckTotalPrices(state, opts) {
    const {
        tripId,
        seats,
        wheelchairs,
    } = opts;

    const legIds = selectLegIds(state, tripId);
    const pricing = legIds.reduce((arr, id) => {
        const { selectedFare } = selectTripData(state, id) || {};
        const lcParams = {
            seats,
            selectedFare,
            tripId: id,
            wheelchairs,
        };

        const lcPricing = liveCheckSelectors.selectLiveCheckPrice(state, lcParams);
        if (!lcPricing) {
            return arr;
        }

        return [...arr, lcPricing];
    }, []);

    if (pricing.length === 0) {
        return undefined;
    }

    return pricing;
}

function selectTripPrices(state, tripId, seats = 1, wheelchairs = 0, showTotalPrice = false) {
    if (!tripId) {
        return {};
    }
    const legIds = selectLegIds(state, tripId);
    return legIds.reduce((obj, legId) => {
        const { fees, pricing: basePricing } = selectTripDataPricing(state, legId) || {};
        const lcPricing = selectLiveCheckPrices(state, {
            seats,
            showTotalPrice,
            tripId: legId,
            wheelchairs,
        });

        if (!basePricing && !lcPricing) {
            return obj;
        }

        if (seats === 1 && wheelchairs === 0) {
            const pricing = lcPricing || basePricing;
            return {
                _perSeatPricing: [...obj._perSeatPricing, ...pricing],
                fees,
                pricing: [...obj.pricing, ...pricing],
            };
        }

        if (lcPricing) {
            return {
                // we can use the base trip pricing here since it is only for sort
                _perSeatPricing: [...obj._perSeatPricing, ...basePricing],
                fees,
                pricing: [...obj.pricing, ...lcPricing],
            };
        }

        // No live check pricing — multiply base pricing by seat count client-side
        const multipliedPricing = basePricing.map((priceObj) => Object.entries(priceObj).reduce((acc, [currency, price]) => ({
            ...acc,
            [currency]: price * seats,
        }), {}),);

        return {
            _perSeatPricing: [...obj._perSeatPricing, ...basePricing],
            fees,
            pricing: [...obj.pricing, ...multipliedPricing],
        };
    }, {
        _perSeatPricing: [],
        fees: [],
        pricing: [],
    });
}

function selectAvailableFares(state, tripId, seats = 1, wheelchairs = 0) {
    const legIds = selectLegIds(state, tripId);

    return legIds.reduce((obj, id) => {
        const { selectedFare } = selectTripData(state, id) || {};
        const lcParams = {
            seats,
            selectedFare,
            tripId: id,
            wheelchairs,
        };

        const availableFares = liveCheckSelectors.selectAvailableFares(state, lcParams) || [];
        const hasOnlyCoachClass = availableFares.length === 1
            && availableFares[0]?.wanderu_type === 'coach';

        if (!!availableFares.length && hasOnlyCoachClass) {
            return obj;
        }

        return { ...obj, [id]: availableFares };
    }, {});
}

export {
    mapTripData,
    selectAllTripIds,
    selectAllTrips,
    selectAvailableFares,
    selectHasFlight,
    selectLegIds,
    selectTrip,
    selectTripArriveData,
    selectTripCarrierData,
    selectTripData,
    selectTripDepartData,
    selectTripDuration,
    selectTripInformation,
    selectTripItinerary,
    selectTripNumberOfMidnightsPassed,
    selectTripPrices,
    selectTripSet,
};
